<template>
  <v-row
    style="position: relative;"
    class="ma-n3"
  >
    <v-col class="pa-0">
      <div
        ref="videoPlayerContainer"
        class="slplayer"
        :class="{ 'slplayer-display': displayMode }"
        @click="handlePlayerContainerClick"
      >
        <video
          ref="videoPlayer"
          autoplay
          preload="auto"
          playsinline="true"
          class="black"

          @pause="HANDLE_PLAYER_PAUSE"
          @ended="handlePlayerEnded"
          @playing="handlePlayerPlaying"
          @loadstart="handleTransitionStart"
          @waiting="handleTransitionStart"
          @emptied="handleTransitionStart"
          @seeking="HANDLE_SEEKING"
          @seeked="HANDLE_SEEKED"
          @volumechange="HANDLE_PLAYER_VOLUME_CHANGE"
          @enterpictureinpicture="HANDLE_PICTURE_IN_PICTURE_CHANGE"
          @leavepictureinpicture="HANDLE_PICTURE_IN_PICTURE_CHANGE"
          @timeupdate="handleTimeUpdate"
        />

        <div
          v-if="displayMode"
          class="display-cover"
          :class="{ 'display-cover-visible': displayCoverVisible }"
        >
          <transition-group
            name="display-tile-fade"
            tag="div"
            class="display-collage"
          >
            <div
              v-for="tile in displayTiles"
              :key="tile.slot"
              class="display-tile"
              :class="[
                `display-tile-motion-${tile.slot % 4}`,
                `display-image-motion-${tile.slot % 3}`,
              ]"
              :style="displayTileStyle(tile)"
              tabindex="0"
            >
              <transition
                name="display-image-fade"
                :duration="3000"
              >
                <img
                  :key="tile.key"
                  :src="tile.url"
                  alt=""
                  @load="handleDisplayTileLoad"
                  @error="handleDisplayTileError(tile, $event)"
                >
              </transition>
              <div class="display-tile-tooltip" role="tooltip">
                <div class="display-tooltip-heading">
                  <span class="display-tooltip-artist">{{ tile.artist }}</span>
                  <span class="display-tooltip-runtime">{{ tile.playTime }}</span>
                </div>
                <div class="display-tooltip-song">
                  {{ tile.song }}
                </div>
                <div class="display-tooltip-details">
                  <span v-if="tile.edition"><b>Edition</b>{{ tile.edition }}</span>
                  <span><b>Year</b>{{ tile.year }}</span>
                </div>
              </div>
            </div>
          </transition-group>
          <div class="display-collage-shade" />
        </div>

        <div
          v-if="displayMode"
          class="display-transition-shade"
          :class="{ 'display-transition-shade-visible': displayTransitionVisible }"
        />

        <button
          v-if="displayMode && !displayFullscreen"
          type="button"
          class="display-fullscreen-prompt"
          @click.stop.prevent="enterDisplayFullscreen"
        >
          <v-icon small>
            fullscreen
          </v-icon>
          Full screen
        </button>

        <v-fade-transition
          transition="fade-transition"
        >
          <v-btn
            v-show="shouldShowSkipIntroButton"
            absolute
            bottom
            right
            large
            class="skip-intro"
            :style="skipIntroButtonStyle"
            @click="SKIP_INTRO"
          >
            Skip Intro
          </v-btn>
        </v-fade-transition>
      </div>

      <v-fade-transition
        transition="fade-transition"
      >
        <v-row
          v-show="ARE_PLAYER_CONTROLS_SHOWN"
          no-gutters
          class="pa-3 hidden-xs-only hoverBar"
        >
          <v-col>
            <v-container fluid>
              <v-row no-gutters>
                <v-col cols="auto">
                  <v-img
                    contain
                    :src="GET_THUMB_URL"
                    class="plex-thumb"
                  />
                </v-col>

                <v-col class="pl-3">
                  <v-container
                    fluid
                    class="pa-0"
                    fill-height
                  >
                    <v-row no-gutters>
                      <v-col>
                        <h1>{{ GET_TITLE }}</h1>
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col>
                        <h3>{{ GET_SECONDARY_TITLE }}</h3>
                      </v-col>
                    </v-row>

                    <v-row no-gutters>
                      <v-col class="text-subtitle-2 primary--text">
                        Playing from {{ GET_PLEX_SERVER ? GET_PLEX_SERVER.name : '' }}
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </v-fade-transition>

      <div
        v-if="!displayMode && $vuetify.breakpoint.mdAndDown"
      >
        <MessageList class="messages-wrapper" />
        <MessageInput />
      </div>

      <div v-if="!displayMode" class="hidden-sm-and-up">
        <v-row
          justify="center"
          class="pa-3"
        >
          <v-col cols="auto">
            <v-img
              contain
              :src="GET_THUMB_URL"
              class="plex-thumb"
            />
          </v-col>

          <v-col class="pl-2">
            <v-container
              class="pa-0"
              fill-height
            >
              <v-row no-gutters>
                <v-col>
                  <h1>{{ GET_TITLE }}</h1>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col>
                  <h3>{{ GET_SECONDARY_TITLE }}</h3>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col class="text-subtitle-2 primary--text">
                  Playing from {{ GET_PLEX_SERVER ? GET_PLEX_SERVER.name : '' }}
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="!AM_I_HOST">
            <v-btn
              block
              color="blue"
              @click="MANUAL_SYNC"
            >
              Manual sync
            </v-btn>
          </v-col>

          <v-col>
            <v-btn
              block
              color="error"
              @click="PRESS_STOP"
            >
              Stop playback
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>

import { mapActions, mapGetters, mapState } from 'vuex';

import initialize from '@/player/init';
import { getControlsOffset } from '@/player';
import linkWithRoom from '@/mixins/linkwithroom';

import 'shaka-player/dist/controls.css';
import 'synclounge-libjass/lib/libjass.css';

export default {
  name: 'WebPlayer',

  components: {
    MessageList: () => import('@/components/MessageList.vue'),
    MessageInput: () => import('@/components/MessageInput.vue'),
  },

  mixins: [
    linkWithRoom,
  ],

  data: () => ({
    videoTimeStamp: 0,
    controlsOffset: 0,
    idleCoverVisible: false,
    idleCoverTimer: null,
    transitionVisible: true,
    transitionTimer: null,
    collagePrefetchTimer: null,
    collageRetryTimer: null,
    collageTimer: null,
    collageRotationQueue: [],
    displayRefreshBusy: false,
    displayFullscreen: false,
    displayTiles: [],
    displayLayouts: [
      {
        left: '1.2%', top: '5%', width: '26%', transform: 'rotate(-2.6deg)', zIndex: 2,
      },
      {
        left: '26%', top: '2%', width: '22%', transform: 'rotate(2deg)', zIndex: 4,
      },
      {
        left: '48%', top: '8%', width: '22%', transform: 'rotate(-2deg)', zIndex: 3,
      },
      {
        left: '73.2%', top: '4.2%', width: '25.5%', transform: 'rotate(2.6deg)', zIndex: 2,
      },
      {
        left: '3.5%', top: '35%', width: '27%', transform: 'rotate(2.2deg)', zIndex: 5,
      },
      {
        left: '29%', top: '31%', width: '32%', transform: 'rotate(-1deg)', zIndex: 6,
      },
      {
        left: '60%', top: '35%', width: '32%', transform: 'rotate(-2.2deg)', zIndex: 5,
      },
      {
        left: '1.2%', top: '67.5%', width: '26%', transform: 'rotate(-1.5deg)', zIndex: 3,
      },
      {
        left: '26%', top: '66%', width: '22%', transform: 'rotate(4deg)', zIndex: 4,
      },
      {
        left: '49%', top: '68%', width: '25%', transform: 'rotate(2deg)', zIndex: 3,
      },
      {
        left: '73.2%', top: '66.5%', width: '25.5%', transform: 'rotate(-2.6deg)', zIndex: 2,
      },
    ],
  }),

  computed: {
    ...mapGetters('slplayer', [
      'GET_THUMB_URL',
      'GET_PLEX_SERVER',
      'GET_TITLE',
      'GET_SECONDARY_TITLE',
      'ARE_PLAYER_CONTROLS_SHOWN',
      'GET_PLAYER_STATE',
      'IS_USING_NATIVE_SUBTITLES',
    ]),

    ...mapGetters('synclounge', [
      'AM_I_HOST',
      'GET_HOST_USER',
    ]),

    ...mapGetters('plexclients', [
      'GET_ACTIVE_MEDIA_METADATA',
      'GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER',
    ]),

    ...mapGetters('settings', [
      'GET_AUTO_SKIP_INTRO',
      'GET_SLPLAYERVOLUME',
    ]),

    ...mapGetters([
      'GET_CONFIG',
    ]),

    ...mapGetters('plexservers', [
      'GET_PLEX_SERVERS',
      'GET_LAST_SERVER_ID',
      'GET_MEDIA_IMAGE_URL',
    ]),

    ...mapState('slplayer', [
      'forceBurnSubtitles',
    ]),

    playerConfig() {
      return {
        streaming: {
          bufferingGoal: this.GET_CONFIG.slplayer_buffering_goal,
        },
      };
    },

    displayMode() {
      return Boolean(this.$route.meta.display);
    },

    displayIdle() {
      return this.hostPlayerState === 'stopped'
        || !this.GET_ACTIVE_MEDIA_METADATA;
    },

    hostPlayerState() {
      return this.GET_HOST_USER ? this.GET_HOST_USER.state : null;
    },

    displayCoverVisible() {
      return this.idleCoverVisible;
    },

    displayTransitionVisible() {
      return !this.idleCoverVisible && this.transitionVisible;
    },

    displayMusicLibrary() {
      const servers = [...this.GET_PLEX_SERVERS]
        .sort((server) => (server.clientIdentifier === this.GET_LAST_SERVER_ID ? -1 : 1));

      return servers.map((server) => {
        const libraries = Object.entries(server.libraries || {});
        const musicVideos = libraries.find(([, library]) => {
          const name = library.title || library.name || '';
          return /music\s*videos?|videos?\s*music/i.test(name);
        });

        if (musicVideos && server.chosenConnection) {
          return {
            machineIdentifier: server.clientIdentifier,
            sectionId: musicVideos[0],
          };
        }

        return null;
      }).find(Boolean) || null;
    },

    skipIntroButtonStyle() {
      return {
        'margin-bottom': `${this.controlsOffset + 12}px`,
      };
    },

    isInIntro() {
      return this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER
        && this.videoTimeStamp >= this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER.startTimeOffset
        && this.videoTimeStamp < this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER.endTimeOffset;
    },

    isInInitialIntroRegion() {
      return this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER
        && this.videoTimeStamp >= this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER.startTimeOffset
        && this.videoTimeStamp - this.GET_ACTIVE_MEDIA_METADATA_INTRO_MARKER.startTimeOffset
          < this.GET_CONFIG.slplayer_initial_skip_intro_visible_period;
    },

    shouldShowSkipIntroButton() {
      return this.AM_I_HOST && this.isInIntro && (this.ARE_PLAYER_CONTROLS_SHOWN
      || this.isInInitialIntroRegion);
    },
  },

  watch: {
    hostPlayerState: {
      handler(state) {
        if (this.displayMode && state === 'stopped') {
          this.stopDisplayPlayback();
        }
      },
      immediate: true,
    },

    GET_PLAYER_STATE(state) {
      if (state === 'stopped' && !this.displayMode) {
        this.$router.push(this.linkWithRoom({ name: 'PlexHome' }));
      }
    },

    GET_ACTIVE_MEDIA_METADATA: {
      async handler(newMetadata) {
        // This handles regular plex clients (nonslplayer) playback changes
        if (newMetadata && !this.displayMode) {
          await this.SET_MEDIA_AS_BACKGROUND(newMetadata);
        }
      },
      immediate: true,
    },

    displayIdle(isIdle) {
      this.updateDisplayIdleVisibility(isIdle);
    },

    ARE_PLAYER_CONTROLS_SHOWN() {
      return this.RERENDER_SUBTITLE_CONTAINER();
    },

    isInIntro: {
      handler() {
        return this.checkAutoSkipIntro();
      },
      immediate: true,
    },

    GET_AUTO_SKIP_INTRO: {
      handler() {
        return this.checkAutoSkipIntro();
      },
      immediate: true,
    },

    async forceBurnSubtitles(forceBurn) {
      if (forceBurn && this.IS_USING_NATIVE_SUBTITLES) {
        await this.UPDATE_PLAYER_SRC_AND_KEEP_TIME();
      }
    },
  },

  async mounted() {
    // TODO: monitor upnext stuff interval probably or idk state change timeugh

    await initialize({
      mediaElement: this.$refs.videoPlayer,
      playerConfig: this.playerConfig,
      videoContainer: this.$refs.videoPlayerContainer,
      overlayConfig: this.getPlayerUiOptions(),
    });

    // New display profiles default to zero volume, but respect a volume the
    // user deliberately saved later instead of muting it again on refresh.
    if (this.displayMode && Number(this.GET_SLPLAYERVOLUME) <= 0) {
      this.$refs.videoPlayer.muted = true;
    }

    await this.INIT_PLAYER_STATE();

    if (this.displayMode) {
      if (this.hostPlayerState === 'stopped') this.stopDisplayPlayback();
      if (this.displayIdle) {
        await this.refreshDisplayCollage(true);
      } else {
        this.collagePrefetchTimer = setTimeout(() => this.refreshDisplayCollage(true), 5000);
      }
      this.scheduleDisplayCollageRotation();
      this.updateDisplayIdleVisibility(this.displayIdle);
    }

    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('resize', this.RERENDER_SUBTITLE_CONTAINER);
    window.addEventListener('resize', this.handleFullscreenChange);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.handleFullscreenChange();
    this.controlsOffset = getControlsOffset(this.$refs?.videoPlayerContainer?.offsetHeight);
  },

  beforeDestroy() {
    clearTimeout(this.idleCoverTimer);
    clearTimeout(this.transitionTimer);
    clearTimeout(this.collagePrefetchTimer);
    clearTimeout(this.collageRetryTimer);
    clearTimeout(this.collageTimer);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('resize', this.RERENDER_SUBTITLE_CONTAINER);
    window.removeEventListener('resize', this.handleFullscreenChange);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.DESTROY_PLAYER_STATE();
  },

  methods: {
    ...mapActions('plexservers', [
      'SET_MEDIA_AS_BACKGROUND',
      'FETCH_RANDOM_ITEM',
    ]),

    ...mapActions('plex', [
      'FETCH_PLEX_DEVICES_IF_NEEDED',
    ]),

    ...mapActions('slplayer', [
      'CHANGE_MEDIA_INDEX',
      'CHANGE_PLAYER_SRC',
      'HANDLE_PLAYER_PLAYING',
      'HANDLE_PLAYER_PAUSE',
      'HANDLE_PLAYER_VOLUME_CHANGE',
      'HANDLE_PLAYER_CLICK',
      'HANDLE_SEEKING',
      'HANDLE_SEEKED',
      'HANDLE_PICTURE_IN_PICTURE_CHANGE',

      'PRESS_STOP',
      'INIT_PLAYER_STATE',
      'DESTROY_PLAYER_STATE',
      'PLAY_PAUSE_VIDEO',
      'SEND_PARTY_PLAY_PAUSE',
      'SKIP_INTRO',
      'RERENDER_SUBTITLE_CONTAINER',
      'UPDATE_PLAYER_SRC_AND_KEEP_TIME',
    ]),

    ...mapActions('synclounge', [
      'MANUAL_SYNC',
    ]),

    stopDisplayPlayback() {
      this.handleTransitionStart();
      const video = this.$refs.videoPlayer;
      if (video && !video.paused) video.pause();
    },

    getCastReceiverId() {
      return window.chrome && window.chrome.cast && window.chrome.cast.media
        ? window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
        : '';
    },

    // This is an action so it's not cached because chromecast stuff loads late
    getPlayerUiOptions() {
      return {
        addBigPlayButton: true,
        controlPanelElements: [
          'mute',
          'volume',
          'time_and_duration',

          'spacer',

          'previous',
          'replay10',
          'play_pause',
          'forward30',
          'next',
          'close',
          'manual_sync',

          'spacer',

          'overflow_menu',
          'fullscreen',
        ],

        overflowMenuButtons: [
          'media',
          'bitrate',
          'audio',

          'subtitle',
          'subtitleoffset',
          'subtitlesize',
          'subtitleposition',
          'subtitlecolor',

          'cast',
          'picture_in_picture',
        ],

        castReceiverAppId: this.getCastReceiverId(),
      };
    },

    onKeyUp(event) {
      const { activeElement } = document;
      const isSeekBar = activeElement && activeElement.classList
        && activeElement.classList.contains('shaka-seek-bar');

      if (event.key === ' ' && activeElement.tagName !== 'INPUT'
        && activeElement.tagName !== 'BUTTON') {
        if (!isSeekBar) {
          // Make spacebar trigger play/pause in locations shaka normally doesn't
          this.PLAY_PAUSE_VIDEO();
        }

        this.SEND_PARTY_PLAY_PAUSE();
      }

      if (this.displayMode && event.key.toLowerCase() === 'f') {
        this.toggleDisplayFullscreen();
      }
    },

    handleFullscreenChange() {
      const screenFullscreen = window.screen
        && window.innerHeight >= window.screen.height - 2;
      const browserChromeHidden = (window.outerHeight - window.innerHeight) < 80
        && (window.outerWidth - window.innerWidth) < 20;
      const browserFullscreen = screenFullscreen || browserChromeHidden;
      this.displayFullscreen = Boolean(document.fullscreenElement || browserFullscreen);
    },

    enterDisplayFullscreen() {
      if (document.fullscreenElement) return;

      // Keep the request synchronous with the physical click. Chrome consumes
      // transient user activation as soon as the handler yields, so do not
      // await any player or Vue work before requesting fullscreen.
      const target = document.documentElement;
      const requestFullscreen = target.requestFullscreen || target.webkitRequestFullscreen;
      if (!requestFullscreen) return;

      try {
        const result = requestFullscreen.call(target, { navigationUI: 'hide' });
        if (result && typeof result.catch === 'function') {
          result.catch((error) => {
            console.warn('Unable to enter dedicated display fullscreen', error);
          });
        }
      } catch (error) {
        console.warn('Unable to enter dedicated display fullscreen', error);
      }
    },

    toggleDisplayFullscreen() {
      if (!document.fullscreenElement) {
        this.enterDisplayFullscreen();
        return;
      }

      const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
      if (exitFullscreen) exitFullscreen.call(document);
    },

    handlePlayerContainerClick(event) {
      if (this.displayMode) {
        if (!document.fullscreenElement) this.enterDisplayFullscreen();
        return;
      }

      this.HANDLE_PLAYER_CLICK(event);
    },

    async handlePlayerEnded() {
      this.handleTransitionStart();

      // A follower can reach the end before the host publishes the next
      // playlist item. Stopping here destroys the fullscreen player element.
      // Keep followers mounted so the next host media loads in-place.
      if (this.AM_I_HOST) {
        await this.PRESS_STOP();
      }
    },

    async handlePlayerPlaying() {
      await this.HANDLE_PLAYER_PLAYING();
      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => {
        this.transitionVisible = false;
      }, 250);
    },

    handleTransitionStart() {
      if (this.displayMode) {
        clearTimeout(this.transitionTimer);
        this.transitionVisible = true;
      }
    },

    updateDisplayIdleVisibility(isIdle) {
      if (!this.displayMode) return;

      clearTimeout(this.idleCoverTimer);
      if (!isIdle) {
        this.idleCoverVisible = false;
        return;
      }

      this.refreshDisplayCollage(this.displayTiles.length === 0);
      this.idleCoverTimer = setTimeout(() => {
        if (this.displayIdle) {
          this.transitionVisible = false;
          this.idleCoverVisible = true;
        }
      }, 7000);
    },

    scheduleDisplayCollageRotation() {
      clearTimeout(this.collageTimer);
      // One staggered replacement every 12-20 seconds means each of the 11
      // positions changes once every 2.2-3.7 minutes. Shuffle the order after
      // every full pass so the movement never develops an obvious pattern.
      const delay = 12000 + Math.floor(Math.random() * 8001);
      this.collageTimer = setTimeout(async () => {
        if (this.displayIdle) {
          if (this.collageRotationQueue.length === 0) {
            this.collageRotationQueue = this.displayLayouts
              .map((layout, slot) => slot)
              .sort(() => Math.random() - 0.5);
          }
          const slot = this.collageRotationQueue.shift();
          await this.refreshDisplayCollage(false, [slot]);
        }
        this.scheduleDisplayCollageRotation();
      }, delay);
    },

    formatDisplayPlayTime(duration) {
      const totalSeconds = Math.max(0, Math.round(Number(duration || 0) / 1000));
      if (!totalSeconds) return 'Unknown';

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (hours) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },

    getDisplayTileMetadata(item) {
      const mediaList = item.media || item.Media || [];
      const media = Array.isArray(mediaList) ? (mediaList[0] || {}) : mediaList;
      const availableDate = item.originallyAvailableAt || item.addedAt || '';
      const yearFromDate = String(availableDate).match(/\b(19|20)\d{2}\b/)?.[0];
      const rawTitle = item.title || item.name || '';
      const titleParts = rawTitle.split(/\s+-\s+/);
      const artistFromTitle = titleParts.length > 1 ? titleParts.shift() : '';
      const songFromTitle = titleParts.length ? titleParts.join(' - ') : rawTitle;

      return {
        artist: item.grandparentTitle || item.originalTitle || artistFromTitle
          || item.studio || item.parentTitle || 'Unknown artist',
        song: songFromTitle || 'Unknown song',
        edition: item.editionTitle || item.edition || media.editionTitle || '',
        year: String(item.year || item.parentYear || yearFromDate || 'Unknown'),
        playTime: this.formatDisplayPlayTime(item.duration || media.duration),
      };
    },

    displayTileStyle(tile) {
      const layout = this.displayLayouts[tile.slot % this.displayLayouts.length];
      const depth = Math.max(2, Math.min(6, Number(layout.zIndex) || 3));
      const durationByDepth = {
        2: 72,
        3: 62,
        4: 53,
        5: 44,
        6: 37,
      };
      const travelByDepth = {
        2: 10,
        3: 13,
        4: 16,
        5: 19,
        6: 22,
      };
      const rotationByDepth = {
        2: 0.3,
        3: 0.45,
        4: 0.6,
        5: 0.8,
        6: 1,
      };
      const motionPaths = [
        [-0.6, -0.35, 0.85, 0.6],
        [0.7, -0.45, -0.8, 0.55],
        [-0.5, 0.75, 0.75, -0.55],
        [0.55, 0.5, -0.7, -0.7],
      ];
      const duration = durationByDepth[depth] + ((tile.slot % 3) * 1.5);
      const imageDuration = (duration * 0.8) + ((tile.slot % 2) * 2);
      const travel = travelByDepth[depth];
      const rotation = rotationByDepth[depth];
      const rotationDirection = tile.slot % 2 === 0 ? 1 : -1;
      const path = motionPaths[tile.slot % motionPaths.length];
      const pixels = (value) => `${Math.round(value * travel * 10) / 10}px`;
      const degrees = (value) => `${Math.round(value * 100) / 100}deg`;

      return {
        ...layout,
        '--display-card-duration': `${duration}s`,
        '--display-image-duration': `${imageDuration}s`,
        '--display-float-from': `${pixels(path[0])} ${pixels(path[1])}`,
        '--display-float-to': `${pixels(path[2])} ${pixels(path[3])}`,
        '--display-rotate-from': degrees(rotation * rotationDirection),
        '--display-rotate-to': degrees(rotation * rotationDirection * -0.8),
      };
    },

    handleDisplayTileLoad(event) {
      event.currentTarget.classList.add('display-tile-image-loaded');
    },

    handleDisplayTileError(tile, event) {
      const image = event.currentTarget;
      image.style.visibility = 'hidden';
      this.retryDisplayTile(tile.slot, 250);
    },

    retryDisplayTile(slot, delay = 1000) {
      clearTimeout(this.collageRetryTimer);
      this.collageRetryTimer = setTimeout(() => {
        if (this.displayRefreshBusy) {
          this.retryDisplayTile(slot);
          return;
        }
        this.refreshDisplayCollage(false, [slot]);
      }, delay);
    },

    preloadDisplayImage(url) {
      return new Promise((resolve) => {
        const image = new Image();
        let timeout;
        const finish = async (loaded) => {
          clearTimeout(timeout);
          image.onload = null;
          image.onerror = null;
          if (!loaded || !image.naturalWidth) {
            resolve(false);
            return;
          }

          try {
            if (typeof image.decode === 'function') await image.decode();
            resolve(true);
          } catch (error) {
            resolve(false);
          }
        };
        timeout = setTimeout(() => finish(false), 10000);
        image.onload = () => finish(true);
        image.onerror = () => finish(false);
        image.src = url;
      });
    },

    async refreshDisplayCollage(initial = false, requestedSlots = null) {
      if (!this.displayMode || this.displayRefreshBusy || (!initial && !this.displayIdle)) return;

      this.displayRefreshBusy = true;

      try {
        await this.FETCH_PLEX_DEVICES_IF_NEEDED();
        const library = this.displayMusicLibrary;
        if (!library) throw new Error('No Music Videos library is available');

        const missingSlots = this.displayLayouts
          .map((layout, slot) => slot)
          .filter((slot) => !this.displayTiles.some((tile) => tile.slot === slot));
        let slots;
        if (requestedSlots) {
          slots = [...requestedSlots];
        } else if (initial || this.displayTiles.length === 0) {
          slots = this.displayLayouts.map((layout, slot) => slot);
        } else {
          slots = missingSlots.slice(0, 2);
        }
        if (slots.length === 0) {
          slots.push(Math.floor(Math.random() * this.displayLayouts.length));
        }

        // Reserve every currently displayed image and each candidate as soon
        // as it is selected. This prevents concurrent slot requests from
        // accepting the same artwork while either image is still loading.
        const usedItems = new Set(this.displayTiles.map(({ itemKey }) => itemKey));
        await Promise.all(slots.map(async (slot) => {
          try {
            const fetchValidTile = async (attemptsRemaining) => {
              const item = await this.FETCH_RANDOM_ITEM(library);
              if (!item) {
                return attemptsRemaining > 1
                  ? fetchValidTile(attemptsRemaining - 1)
                  : null;
              }

              const itemKey = item.thumb || item.art || item.ratingKey;
              if (usedItems.has(itemKey)) {
                return attemptsRemaining > 1
                  ? fetchValidTile(attemptsRemaining - 1)
                  : null;
              }

              const mediaUrl = item.thumb || item.art;
              if (!mediaUrl) {
                return attemptsRemaining > 1
                  ? fetchValidTile(attemptsRemaining - 1)
                  : null;
              }
              usedItems.add(itemKey);
              const url = this.GET_MEDIA_IMAGE_URL({
                machineIdentifier: library.machineIdentifier,
                mediaUrl,
                width: 640,
                height: 360,
              });
              const loaded = await this.preloadDisplayImage(url);
              if (!loaded && attemptsRemaining > 1) {
                usedItems.delete(itemKey);
                return fetchValidTile(attemptsRemaining - 1);
              }
              if (!loaded) {
                usedItems.delete(itemKey);
                return null;
              }

              return {
                slot,
                itemKey,
                key: `${slot}-${itemKey}-${Date.now()}-${Math.random()}`,
                url,
                ...this.getDisplayTileMetadata(item),
              };
            };

            const replacement = await fetchValidTile(12);
            if (replacement) this.applyDisplayReplacement(replacement);
          } catch (error) {
            console.warn(`Unable to load collage slot ${slot}`, error);
          }
        }));
      } catch (error) {
        console.warn('Unable to rotate dedicated display collage', error);
      } finally {
        this.displayRefreshBusy = false;
        if (this.displayIdle && this.displayTiles.length < this.displayLayouts.length) {
          clearTimeout(this.collageRetryTimer);
          this.collageRetryTimer = setTimeout(() => this.refreshDisplayCollage(), 5000);
        }
      }
    },

    applyDisplayReplacement(replacement) {
      const nextTiles = [...this.displayTiles];
      const currentIndex = nextTiles.findIndex(({ slot }) => slot === replacement.slot);
      if (currentIndex === -1) nextTiles.push(replacement);
      else nextTiles.splice(currentIndex, 1, replacement);
      this.displayTiles = nextTiles.sort((a, b) => a.slot - b.slot);
    },

    handleTimeUpdate() {
      this.videoTimeStamp = (this.$refs?.videoPlayer?.currentTime ?? 0) * 1000;
    },

    async checkAutoSkipIntro() {
      if (this.isInIntro && this.GET_AUTO_SKIP_INTRO) {
        await this.SKIP_INTRO();
      }
    },
  },
};
</script>

<style scoped>
.slplayer-container {
  margin-top: -12px;
  margin-bottom: -12px;
}

.slplayer video {
  width: 100%;
  height: 100%;
}

.slplayer {
  height: calc(100vh - 64px);
  background: #000;
  overflow: hidden;
  position: relative;
}

.slplayer-display {
  position: fixed;
  inset: 0;
  z-index: 100;
  width: 100vw;
  height: 100vh !important;
}

.display-cover {
  position: absolute;
  inset: 0;
  z-index: 3;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 0% 48%, rgb(36 43 57 / 54%), transparent 38%),
    radial-gradient(ellipse at 100% 52%, rgb(50 37 49 / 46%), transparent 38%),
    radial-gradient(circle at 50% 45%, #1c1d21 0%, #0d0e11 64%, #050506 100%);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 700ms ease, visibility 0s linear 700ms;
}

.display-cover-visible {
  opacity: 1;
  visibility: visible;
  transition: opacity 150ms ease, visibility 0s linear 0s;
}

.display-cover::before {
  position: absolute;
  inset: -7%;
  z-index: 0;
  content: "";
  background:
    radial-gradient(ellipse at 4% 54%, rgb(63 72 91 / 22%), transparent 30%),
    radial-gradient(ellipse at 96% 46%, rgb(84 55 67 / 18%), transparent 31%),
    radial-gradient(circle at 26% 32%, rgb(48 54 65 / 24%), transparent 34%),
    radial-gradient(circle at 73% 68%, rgb(54 38 45 / 20%), transparent 38%);
  animation: display-background-drift 58s ease-in-out infinite alternate;
  will-change: transform;
}

.display-transition-shade {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: #000;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 520ms ease, visibility 0s linear 520ms;
}

.display-transition-shade-visible {
  opacity: 1;
  visibility: visible;
  transition: opacity 180ms ease, visibility 0s linear 0s;
}

.display-fullscreen-prompt {
  position: absolute;
  right: 22px;
  bottom: 22px;
  z-index: 30;
  padding: 8px 13px;
  color: rgb(255 255 255 / 82%);
  font: inherit;
  font-size: 13px;
  letter-spacing: 0.02em;
  cursor: pointer;
  background: rgb(0 0 0 / 58%);
  border: 1px solid rgb(255 255 255 / 20%);
  border-radius: 18px;
  box-shadow: 0 8px 22px rgb(0 0 0 / 45%);
  backdrop-filter: blur(7px);
}

.display-fullscreen-prompt:hover,
.display-fullscreen-prompt:focus-visible {
  color: #fff;
  background: rgb(20 20 20 / 82%);
}

.display-collage {
  position: absolute;
  inset: 0;
  z-index: 1;
  perspective: 1200px;
}

.display-tile {
  position: absolute;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: 12px;
  background: #111;
  box-shadow:
    0 2px 3px rgb(255 255 255 / 7%) inset,
    0 12px 20px rgb(0 0 0 / 58%),
    0 28px 58px rgb(0 0 0 / 70%);
  animation: display-card-float-a var(--display-card-duration, 47s) ease-in-out infinite alternate;
  backface-visibility: hidden;
  cursor: help;
  outline: none;
  pointer-events: auto;
  will-change: translate, rotate, filter;
}

.display-tile:hover,
.display-tile:focus-visible {
  border-color: rgb(255 255 255 / 34%);
  box-shadow:
    0 2px 3px rgb(255 255 255 / 10%) inset,
    0 16px 28px rgb(0 0 0 / 66%),
    0 34px 68px rgb(0 0 0 / 78%);
}

.display-tile-motion-1 {
  animation-name: display-card-float-b;
  animation-delay: -19s;
}

.display-tile-motion-2 {
  animation-name: display-card-float-c;
  animation-delay: -37s;
}

.display-tile-motion-3 {
  animation-name: display-card-float-d;
  animation-delay: -28s;
}

.display-tile::after {
  position: absolute;
  inset: 0;
  z-index: 1;
  content: "";
  border-radius: inherit;
  box-shadow:
    0 1px 0 rgb(255 255 255 / 9%) inset,
    0 -18px 30px rgb(0 0 0 / 12%) inset;
}

.display-tile img {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;
  object-fit: cover;
  transform: scale(1.025);
  animation: display-drift-a var(--display-image-duration, 38s) ease-in-out infinite alternate;
  transition: opacity 180ms ease-out;
  will-change: transform;
}

.display-tile-tooltip {
  position: absolute;
  right: clamp(8px, 1vw, 14px);
  bottom: clamp(8px, 1vw, 14px);
  left: clamp(8px, 1vw, 14px);
  z-index: 3;
  padding: clamp(8px, 0.9vw, 13px);
  color: #f8f8f8;
  background: linear-gradient(145deg, rgb(7 9 14 / 91%), rgb(18 20 27 / 84%));
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: 9px;
  box-shadow: 0 10px 28px rgb(0 0 0 / 55%);
  opacity: 0;
  pointer-events: none;
  transform: translateY(9px);
  transition: opacity 260ms ease, transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
  backdrop-filter: blur(10px) saturate(1.12);
}

.display-tile:hover .display-tile-tooltip,
.display-tile:focus-visible .display-tile-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.display-tooltip-heading {
  display: flex;
  gap: 8px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 3px;
}

.display-tooltip-artist {
  overflow: hidden;
  color: #ffd88a;
  font-size: clamp(10px, 0.72vw, 13px);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.display-tooltip-runtime {
  flex: 0 0 auto;
  color: rgb(255 255 255 / 72%);
  font-size: clamp(10px, 0.68vw, 12px);
  font-variant-numeric: tabular-nums;
}

.display-tooltip-song {
  overflow: hidden;
  margin-bottom: 7px;
  font-size: clamp(13px, 1vw, 18px);
  font-weight: 650;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.display-tooltip-details {
  display: flex;
  gap: 14px;
  color: rgb(255 255 255 / 78%);
  font-size: clamp(9px, 0.64vw, 12px);
  white-space: nowrap;
}

.display-tooltip-details span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.display-tooltip-details b {
  margin-right: 5px;
  color: rgb(255 255 255 / 48%);
  font-weight: 600;
  text-transform: uppercase;
}

.display-tile img.display-tile-image-loaded {
  opacity: 1;
}

.display-image-motion-1 img {
  animation-name: display-drift-b;
  animation-delay: -9s;
}

.display-image-motion-2 img {
  animation-name: display-drift-c;
  animation-delay: -17s;
}

.display-collage-shade {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: radial-gradient(ellipse at center, transparent 54%, rgb(0 0 0 / 24%) 100%);
}

.display-tile-fade-enter-active,
.display-tile-fade-leave-active {
  transition: opacity 1600ms ease, filter 1600ms ease;
}

.display-tile-fade-enter,
.display-tile-fade-leave-to {
  opacity: 0;
  filter: brightness(0.5);
}

.display-tile img.display-image-fade-enter-active,
.display-tile img.display-image-fade-leave-active {
  transition:
    opacity 2800ms cubic-bezier(0.4, 0, 0.2, 1),
    filter 2800ms cubic-bezier(0.4, 0, 0.2, 1);
}

.display-tile img.display-image-fade-enter,
.display-tile img.display-image-fade-leave-to {
  opacity: 0;
  filter: brightness(0.78) saturate(0.9);
}

@keyframes display-drift-a {
  from { transform: scale(1.025) translate3d(-0.35%, -0.2%, 0); }
  to { transform: scale(1.075) translate3d(0.45%, 0.3%, 0); }
}

@keyframes display-drift-b {
  from { transform: scale(1.065) translate3d(0.4%, -0.35%, 0); }
  to { transform: scale(1.025) translate3d(-0.4%, 0.3%, 0); }
}

@keyframes display-drift-c {
  from { transform: scale(1.035) translate3d(-0.3%, 0.35%, 0); }
  to { transform: scale(1.08) translate3d(0.35%, -0.25%, 0); }
}

@keyframes display-card-float-a {
  from {
    translate: var(--display-float-from, -9px -5px);
    rotate: var(--display-rotate-from, 0deg);
    filter: brightness(0.98);
  }

  to {
    translate: var(--display-float-to, 13px 9px);
    rotate: var(--display-rotate-to, 0deg);
    filter: brightness(1.03);
  }
}

@keyframes display-card-float-b {
  from {
    translate: var(--display-float-from, 11px -7px);
    rotate: var(--display-rotate-from, 0deg);
    filter: brightness(1.02);
  }

  to {
    translate: var(--display-float-to, -12px 8px);
    rotate: var(--display-rotate-to, 0deg);
    filter: brightness(0.97);
  }
}

@keyframes display-card-float-c {
  from {
    translate: var(--display-float-from, -7px 11px);
    rotate: var(--display-rotate-from, 0deg);
    filter: brightness(0.97);
  }

  to {
    translate: var(--display-float-to, 10px -8px);
    rotate: var(--display-rotate-to, 0deg);
    filter: brightness(1.025);
  }
}

@keyframes display-card-float-d {
  from {
    translate: var(--display-float-from, 8px 7px);
    rotate: var(--display-rotate-from, 0deg);
    filter: brightness(1.025);
  }

  to {
    translate: var(--display-float-to, -10px -10px);
    rotate: var(--display-rotate-to, 0deg);
    filter: brightness(0.98);
  }
}

@keyframes display-background-drift {
  from { transform: translate3d(-1.2%, -0.8%, 0) scale(1.02); }
  to { transform: translate3d(1.1%, 0.9%, 0) scale(1.07); }
}

@media (prefers-reduced-motion: reduce) {
  .display-tile img {
    animation: none;
  }
}

@media screen and (max-width: 1264px) {
  div.slplayer {
    height: calc(0.5625 * 100vw);
  }
}

.hoverBar {
  position: absolute;
  background:
    -webkit-gradient(
      linear,
      left top,
      left bottom,
      from(rgb(0 0 0 / 80%)),
      color-stop(60%, rgb(0 0 0 / 35%)),
      to(transparent)
    );
  background: linear-gradient(180deg, rgb(0 0 0 / 80%) 0, rgb(0 0 0 / 35%) 60%, transparent);
  top: 0;
  left: 0;
  width: 100%;
}

.plex-thumb {
  height: 80px;
  width: auto;
  vertical-align: middle;
  margin-left: auto;
  margin-right: auto;
}

.v-btn.skip-intro {
  z-index: 2;
}

.v-btn.skip-intro.fade-transition-leave-active {
  transition-duration: 0.6s !important;
}
</style>

<style>
.messages-wrapper {
  max-height: calc(100vh - (0.5625 * 100vw) - 150px);
  overflow: scroll;
}

.is-fullscreen .messages-wrapper {
  height: calc(100vh - (0.5625 * 100vw));
}

/* Having to put shaka styling here since scoped rules don't seem to apply to them
  likely because its added dynamically */

.shaka-slplayer-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.shaka-play-button {
  padding: 50px !important;
}

.shaka-spinner {
  padding: 57px !important;
}

.libjass-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
